import React, { useEffect, useState } from 'react';
import Box from 'ui/Box';
import { useSelector } from 'react-redux';
import { SelectedProduct, getSelectedProduct } from 'ducks/products';
import {
  CoreApi,
  ProductEntity,
  ProductInfoComponent,
  ProductType
} from '@inter-ikea-storageone/range';
import styled from 'styled-components';
import { ProductThumb } from 'selectedMenu/components';
import Body from 'ui/Typography/Body';
import LeftCornerSolution from './icons/LeftCornerFrame.svg?react';
import RightCornerSolution from './icons/RightCornerFrame.svg?react';
import LeftLDivider from './icons/LeftDivider.svg?react';
import RightLDivider from './icons/RightDivider.svg?react';
import { useCore } from 'contexts/core';
import { getSelectedEntity } from 'ducks/core';
import { useIntl } from 'react-intl';
import {
  findAllowedCornerFrameOrientations,
  findAllowedLDividerOrientations
} from '../utils/findAllowedOrientations';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { reportChangeCornerSolutionOrientation } from 'analytics/reporters';
import KompisToolTip from 'ui/KompisToolTip/KompisToolTip';
import { isMobile } from 'utils/platform';
import { useBehaviorTracker } from 'contexts/behaviors';
import { ComponentIds, Entity, HierarchyUtils, ProductComponent } from '@inter-ikea-core/core';

const Thumbnails = styled.ul`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  margin: 0;
  padding-left: ${({ theme }) => (isMobile() ? theme.space.md : 0)};
`;

const Image = styled.div`
  width: ${({ theme }) => theme.space.xxxl};
  height: ${({ theme }) => theme.space.xxxxl};
  padding-bottom: ${({ theme }) => theme.space.xs};
  display: flex;
  justify-content: center;
`;

const StyledButton = styled.button`
  position: relative;
  background: transparent;
  cursor: pointer;
  width: 100%;
  border: none;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }
`;

export const supportsChangingOrientation = (productType: string) =>
  productType === ProductType.PAX_CORNER_SOLUTION_ASM || productType === ProductType.PAX_L_DIVIDER;

const getSolutionIds = (
  selectedProduct: SelectedProduct | null,
  selectedEntity?: ProductEntity
) => {
  if (!selectedEntity || !selectedProduct) return { leftSolutionId: '', rightSolutionId: '' };

  const split = selectedEntity.productId.split('-');
  const leftSolutionId =
    selectedProduct?.type === ProductType.PAX_CORNER_SOLUTION_ASM
      ? `${split[0]}-${split[1]}-asm`
      : `${split[0]}-mirrored`;
  const rightSolutionId =
    selectedProduct?.type === ProductType.PAX_CORNER_SOLUTION_ASM
      ? `${split[0]}-${split[1]}-mirrored-asm`
      : split[0];

  return { leftSolutionId, rightSolutionId };
};

// Helper to find L-dividers within a corner solution
const findLDividers = (coreApi: CoreApi, entity: ProductEntity) => {
  const foundEntity = coreApi.entities.find(entity.entityId);

  if (!foundEntity) {
    return [];
  }

  return HierarchyUtils.descendants(foundEntity).filter(
    (descendant: Entity) =>
      descendant.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.PAX_L_DIVIDER
  );
};

// Helper to get the mirrored L-divider product ID
const getMirroredLDividerProductId = (productId: string) => {
  if (productId.includes('-mirrored')) {
    return productId.replace('-mirrored', '');
  } else {
    const split = productId.split('-');
    return `${split[0]}-mirrored`;
  }
};

const ChangeOrientation = ({ ...props }) => {
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const [leftOrientationEnabled, setLeftOrientationEnabled] = useState(true);
  const [rightOrientationEnabled, setRightOrientationEnabled] = useState(true);
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedEntity = useSelector(getSelectedEntity);
  const behaviorTracker = useBehaviorTracker();

  const { leftSolutionId, rightSolutionId } = getSolutionIds(selectedProduct, selectedEntity);

  useEffect(() => {
    const checkAllowedOrientation = () => {
      if (!selectedEntity) return;
      const findAllowedOrientations =
        selectedProduct?.type === ProductType.PAX_CORNER_SOLUTION_ASM
          ? findAllowedCornerFrameOrientations
          : findAllowedLDividerOrientations;
      const { left, right } = findAllowedOrientations(coreApi, selectedEntity, {
        left: leftSolutionId,
        right: rightSolutionId
      });
      setLeftOrientationEnabled(left);
      setRightOrientationEnabled(right);
    };
    checkAllowedOrientation();
    return coreApi.subscribe('history', checkAllowedOrientation);
  }, [coreApi, selectedEntity, leftSolutionId, rightSolutionId, selectedProduct]);

  if (!selectedProduct || !supportsChangingOrientation(selectedProduct.type)) {
    return null;
  }

  if (!leftSolutionId || !rightSolutionId) {
    return null;
  }

  const onSelect = (solutionId: string) =>
    behaviorTracker.custom(() => {
      if (!selectedEntity || solutionId === selectedEntity.productId) {
        return;
      }
      const newSolutionId = solutionId === leftSolutionId ? leftSolutionId : rightSolutionId;

      if (selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM) {
        // Find and store L-dividers before replacement
        const lDividers = findLDividers(coreApi, selectedEntity);
        const lDividerInfo = lDividers.map(divider => ({
          productId:
            divider.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef ||
            '',
          entityId: divider.id
        }));

        coreApi.validations['WallVisibilityValidation']?.disable();
        const placementResult = coreApi.products.replace(selectedEntity.entityId, newSolutionId);
        if (placementResult?.replacedEntityResult.placedEntity) {
          // Resnap corner solution so it correctly snaps to corner after changing orientation
          coreApi.entities.resnapEntity(placementResult.replacedEntityResult.placedEntity);

          const doors = coreApi.entities.getDescendantsWithComponent(
            placementResult.replacedEntityResult.placedEntity,
            ComponentIds.DOOR_COMPONENT_ID
          );

          doors.forEach(door => {
            // Doors can end up in strange position after replacing corner solution so we resnap them
            coreApi.placementService.snapToTarget(
              door,
              placementResult.replacedEntityResult.placedEntity!
            );
          });

          // Replace and reposition L-dividers with mirrored versions if needed
          if (lDividerInfo.length > 0) {
            lDividerInfo.forEach((divider: { productId: string; entityId: number }) => {
              const mirroredDividerProductId = getMirroredLDividerProductId(divider.productId);

              // Replace the L-divider with its mirrored version
              const dividerReplacement = coreApi.products.replace(
                divider.entityId,
                mirroredDividerProductId
              );

              if (dividerReplacement?.replacedEntityResult.placedEntity) {
                // Resnap the divider to the new corner solution
                coreApi.placementService.snapToTarget(
                  dividerReplacement.replacedEntityResult.placedEntity,
                  placementResult.replacedEntityResult.placedEntity!
                );
              }
            });
          }

          coreApi.entities.select(placementResult.replacedEntityResult.placedEntity);
          reportChangeCornerSolutionOrientation();
        }
        coreApi.validations['WallVisibilityValidation']?.enable();
      } else {
        const replacePlacementResult = coreApi.products.replace(
          selectedEntity.entityId,
          newSolutionId
        );

        if (
          replacePlacementResult?.replacedEntityResult.valid &&
          replacePlacementResult.replacedEntityResult.placedEntity
        ) {
          coreApi.entities.select(replacePlacementResult.replacedEntityResult.placedEntity);
        }
      }
    });

  const leftToolTip =
    selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM
      ? formatMessage({ id: 'storageone.leftCornerUnit' })
      : formatMessage({ id: 'storageone.left' });
  const rightToolTip =
    selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM
      ? formatMessage({ id: 'storageone.rightCornerUnit' })
      : formatMessage({ id: 'storageone.right' });

  return (
    <Box mt="s">
      {!isMobile() && (
        <Body mb="md" fontWeight="bold" type={ThemeFontStyleTypeEnum.bodyMedium}>
          {formatMessage({ id: 'storageone.changeOrientation' })}
        </Body>
      )}
      <Thumbnails {...props}>
        <ProductThumb
          data-disabled={!leftOrientationEnabled}
          data-testid={leftSolutionId}
          onClick={() => onSelect(leftSolutionId)}
          data-selected={selectedEntity?.productId === leftSolutionId}
        >
          <KompisToolTip
            text={leftToolTip}
            uniqueId="change-orientation-left"
            disabled={isMobile()}
          >
            <StyledButton>
              <Image>
                {selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM && (
                  <LeftCornerSolution />
                )}
                {selectedProduct.type === ProductType.PAX_L_DIVIDER && <LeftLDivider />}
              </Image>
            </StyledButton>
          </KompisToolTip>
        </ProductThumb>
        <ProductThumb
          data-disabled={!rightOrientationEnabled}
          data-testid={rightSolutionId}
          onClick={() => onSelect(rightSolutionId)}
          data-selected={selectedEntity?.productId === rightSolutionId}
        >
          <KompisToolTip
            text={rightToolTip}
            uniqueId="change-orientation-right"
            disabled={isMobile()}
          >
            <StyledButton>
              <Image>
                {selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM && (
                  <RightCornerSolution />
                )}
                {selectedProduct.type === ProductType.PAX_L_DIVIDER && <RightLDivider />}
              </Image>
            </StyledButton>
          </KompisToolTip>
        </ProductThumb>
      </Thumbnails>
    </Box>
  );
};

export default ChangeOrientation;
